import React from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';

/* Shared components */
import NrtvRedirect from './components/base/redirect';
import Editor from './components/base/smartlinks/editor';
import {
  Dashboard as PublisherDashboard,
  TopPublisherMegatable as MerchTopPublisherMegatable,
} from './components/base/common/flagged-components';

/* Admin components */
import AdminWelcome from './components/admin/flagged-welcome';
import AdminDashboard from './components/admin/dashboard/dashboard';
import SmartlinkEditor from './components/admin/smartlinks/smartlink-editor';
import MatchQueuesList from './components/admin/product-match/match-queues-list';
import MatchQueue from './components/admin/product-match/match-queue';
import FeedComparison from './components/admin/feeds/feed-comparison';
import AuctionConfig from './components/admin/bid-center/auction-config';
import BulkMatch from './components/admin/bid-center/bulk-match';
import Collections from './components/admin/collections/collections';
import ContentManager from './components/admin/creator-workspace/content-manager';
import CategoryBreakdown from './components/admin/creator-workspace/category-breakdown';

/* Merchant components */
import MerchantArticleSearch from './components/merchant/article-search/article-search';
import MerchantDashboard from './components/merchant/dashboard/dashboard';
import {
  MerchArticleMegatable,
  MerchRecommendationMegatable,
} from './components/merchant/megatable/megatable-extensions';
import MerchantStrategies from './components/merchant/strategies/merchant-strategies';

/* Publisher components */
import Analytics from './components/publisher/megatable/megatable-analytics';
import {
  ArticleMegatable,
  MerchantMegatable,
  ProductMegatable,
  SmartlinkMegatable,
} from './components/publisher/megatable/megatable-extensions';
import Insights from './components/publisher/trends/insights';
import RetailerOverview from './components/publisher/rates/retailer-overview';
import RetailerProfile from './components/publisher/rates/retailer-profile';
import StrategyProducts from './components/publisher/rates/strategy-products';
import Orders from './components/publisher/orders/orders';
import LinkCreation from './components/creator/link-creation';
import CreateLinks from './components/publisher/links/create-links';
import ManageLinks from './components/publisher/links/manage-links';
import StandalonePayments from './components/base/account/standalone-payments';
import HomePage from './components/publisher/home-page/home-page';

/* Static components */
import ChangePassword from './components/base/authentication/change-password';
import NewLogin from './components/base/authentication/new-login';
import AccountSettings from './components/base/account/account-settings';
import PrivacyPolicy from './components/base/authentication/privacy-policy';
import { session } from './services/session';
import CampaignCenter from './components/merchant/campaign-center';
import CampaignPerformance from './components/merchant/campaign-performance';
import CampaignTracking from './components/merchant/campaign-tracking/campaign-tracking';
import AccountOverview from './components/admin/accounts/account-overview';
import ManageAccount from './components/admin/accounts/manage-account';
import CreateCollection from './components/admin/collections/create-collection';
import {
  MerchOverview,
  MerchEdOverview,
  PubOverview,
} from './components/admin/bid-center/overview-extension';
import ForgotPassword from './components/base/authentication/forgot-password';
import ResetPassword from './components/base/authentication/reset-password';
import TermsConditions from './components/base/authentication/terms-conditions';

import ArticleSearch from './components/base/article-search';

import Widgets from './components/admin/widgets/widgets';
import CampaignCreation from './components/merchant/campaign-creation/campaign-creation';
import StrategyCreation from './components/merchant/strategy-creation/strategy-creation';

export default props => {
  return (
    <Switch>
      <Route exact path="/v2" render={() => <NrtvRedirect {...props} />} />

      {/* Admin routes */}
      <Route exact path="/v2/admin" component={AdminWelcome} />
      <Route exact path="/v2/admin/dashboard" component={AdminDashboard} />
      <Route exact path="/v2/admin/smartlinks/editor" component={Editor} />
      <Route exact path="/v2/admin/smartlinks/:smartlinkId?/edit" component={SmartlinkEditor} />
      <Route exact path="/v2/admin/product-match/match-queues" component={MatchQueuesList} />
      <Route exact path="/v2/admin/product-match/match-queues/:queueSlug?" component={MatchQueue} />
      <Route exact path="/v2/admin/feed-comparison" component={FeedComparison} />
      <Route exact path="/v2/admin/feed-comparison/:merchantId?/" component={FeedComparison} />
      <Route
        exact
        path="/v2/admin/feed-comparison/:merchantId?/:fieldName?/"
        component={FeedComparison}
      />
      <Route exact path="/v2/admin/bid-center/auction-config-editor/" component={AuctionConfig} />
      <Route exact path="/v2/admin/bid-center/bulk-match/" component={BulkMatch} />
      <Route
        exact
        path="/v2/admin/bid-center/merchant-overview"
        render={() => <MerchOverview {...props} />}
      />
      <Route
        exact
        path="/v2/admin/bid-center/merch-ed-overview"
        render={() => <MerchEdOverview {...props} />}
      />
      <Route exact path="/v2/admin/bid-center/publisher-overview" component={PubOverview} />
      <Route exact path="/v2/admin/collections" component={Collections} />
      <Route
        exact
        path="/v2/admin/collections/create/:creationType?"
        component={CreateCollection}
      />
      <Route exact path="/v2/admin/creator-workspace/content-manager" component={ContentManager} />
      <Route
        exact
        path="/v2/admin/creator-workspace/content-manager/:categoryId?"
        component={CategoryBreakdown}
      />
      <Route exact path="/v2/admin/accounts/overview" component={AccountOverview} />
      <Route
        exact
        path="/v2/admin/accounts/create/:accountType?/:accountId?"
        component={ManageAccount}
      />
      <Route
        exact
        path="/v2/admin/accounts/edit/:accountType?/:accountId?"
        component={ManageAccount}
      />
      <Route exact path="/v2/admin/accounts/widgets/jstags" render={() => <Widgets {...props} />} />

      {/* Catch the rest of the admin pages to redirect to v1 */}
      <Route exact path="/v2/admin/*" component={NrtvRedirect} />

      {/* Merchant routes */}
      <Route
        exact
        path="/v2/merchant/:merchantId?/article-search/:searchType?"
        render={() => <MerchantArticleSearch {...props} />}
      />
      <Route
        exact
        path="/v2/merchant/:merchantId?/desk"
        render={() => <MerchantDashboard {...props} />}
      />
      <Route
        exact
        path="/v2/merchant/:merchantId?/megatables/articles"
        render={() => <MerchArticleMegatable {...props} />}
      />
      <Route
        exact
        path="/v2/merchant/:merchantId?/megatables/recommendations"
        render={() => <MerchRecommendationMegatable {...props} />}
      />
      <Route
        exact
        path="/v2/merchant/:merchantId?/megatables/publishers"
        render={() => <MerchTopPublisherMegatable {...props} />}
      />
      <Route
        exact
        path="/v2/merchant/:merchantId?/megatables/campaign-performance/:strategyId?/:tab?"
        render={() => <CampaignTracking {...props} />}
      />
      <Route
        path="/v2/merchant/:merchantId/campaign-performance/:strategyId"
        render={() => <CampaignPerformance {...props} />}
      />
      <Route
        exact
        path="/v2/merchant/:merchantId?/campaigns/:action?"
        render={() => <MerchantStrategies {...props} />}
      />
      <Route
        exact
        path="/v2/merchant/:merchantId/campaign-center/budgets/new"
        render={() => <CampaignCreation {...props} />}
      />
      <Route
        exact
        path="/v2/merchant/:merchantId/campaign-center/budgets/:strategyGroupId/edit"
        render={() => <CampaignCreation {...props} />}
      />

      <Route
        exact
        path="/v2/merchant/:merchantId/campaign-center/budgets/:strategyGroupId/campaigns/new"
        render={() => <StrategyCreation {...props} />}
      />

      <Route
        exact
        path="/v2/merchant/:merchantId/campaign-center/budgets/:strategyGroupId/campaigns/:strategyId/edit"
        render={() => <StrategyCreation {...props} />}
      />
      <Route
        path="/v2/merchant/:merchantId/campaign-center"
        render={() => <CampaignCenter {...props} />}
      />
      {/* Publisher routes */}
      {session.isAdminView ? (
        <Redirect
          from="/v2/publisher/:publisherId?/new-recs/cart-explorer"
          to="/v2/publisher/:publisherId?/new-recs/product-research"
        />
      ) : (
        <Route
          exact
          path="/v2/publisher/:publisherId?/new-recs/cart-explorer"
          component={Insights}
        />
      )}
      <Route
        exact
        path="/v2/publisher/:publisherId?/new-recs/product-research"
        component={Insights}
      />
      <Route
        exact
        path="/v2/publisher/:publisherId?/dashboard"
        render={() => <PublisherDashboard {...props} />}
      />
      <Route
        exact
        path="/v2/publisher/:publisherId?/home-page"
        render={() => <HomePage {...props} />}
      />
      <Route
        exact
        path="/v2/publisher/:publisherId?/megatables/articles"
        render={() => <ArticleMegatable {...props} />}
      />
      <Route
        exact
        path="/v2/publisher/:publisherId?/megatables/merchants"
        render={() => <MerchantMegatable {...props} />}
      />
      <Route
        exact
        path="/v2/publisher/:publisherId?/megatables/products"
        render={() => <ProductMegatable {...props} />}
      />
      <Route
        exact
        path="/v2/publisher/:publisherId?/megatables/smartlinks"
        render={() => <SmartlinkMegatable {...props} />}
      />
      <Route path="/v2/publisher/:publisherId?/payments" component={StandalonePayments} />
      <Route path="/v2/publisher/:publisherId?/analytics" render={() => <Analytics {...props} />} />
      <Route exact path="/v2/publisher/:publisherId?/new-recs/rates" component={RetailerOverview} />
      <Route
        exact
        path="/v2/publisher/:publisherId?/new-recs/rates/:retailerId?"
        component={RetailerProfile}
      />
      <Route
        exact
        path="/v2/publisher/:publisherId?/new-recs/rates/:retailerId?/strategies/:strategyId?"
        component={StrategyProducts}
      />
      <Route exact path="/v2/publisher/:publisherId?/new-recs/search-trends" component={Insights} />
      <Route
        exact
        path="/v2/publisher/:publisherId?/smartlinks/editor"
        render={() => <Editor {...props} />}
      />
      <Route
        exact
        path="/v2/publisher/:publisherId?/smartlinks/:smartlinkId?/edit"
        component={SmartlinkEditor}
      />
      <Route
        exact
        path="/v2/publisher/terms-conditions"
        render={() =>
          !session.termsAccepted ? <TermsConditions {...props} /> : <Redirect to="/v2" />
        }
      />
      <Route exact path="/v2/publisher/:publisherId?/orders" component={Orders} />
      {props.isMobile && (
        <Route
          exact
          path="/v2/publisher/:publisherId?/link-creation"
          render={() => <LinkCreation {...props} />}
        />
      )}
      <Route exact path="/v2/publisher/:publisherId?/links/my-links" component={ManageLinks} />
      <Route path="/v2/publisher/:publisherId?/links/create-links" component={CreateLinks} />
      <Route
        path="/v2/publisher/:publisherId?/account-settings/:activeTab"
        component={AccountSettings}
      />
      <Route
        path="/v2/merchant/:merchantId?/account-settings/:activeTab"
        component={AccountSettings}
      />

      {/* Static routes */}
      <Route exact path="/v2/login" render={() => <NewLogin {...props} />} />
      <Route exact path="/v2/create-user" render={() => <NewLogin {...props} newUser={true} />} />
      {/* TODO: Deprecate /v2/change-password */}
      <Route exact path="/v2/change-password" component={ChangePassword} />
      <Route exact path="/v2/privacy-policy" render={() => <PrivacyPolicy {...props} />} />
      <Route exact path="/v2/reset-password-request" render={() => <ForgotPassword {...props} />} />
      <Route
        exact
        path="/v2/reset-password-completion"
        render={() => <ResetPassword {...props} />}
      />
      <Route exact path="/v2/article-search" render={() => <ArticleSearch {...props} />} />

      <Route render={() => <Redirect to="/v2" />} />
    </Switch>
  );
};
